/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { DESKTOP_LIST_IMAGE_CONFIG, OverflowCarousel } from '@coursera-web/cds-labs';

import type { CoachRecommendedProductCardsFragment } from 'bundles/ai-coach-platform/queries/__generated__/coachRecommendedProductCardsFragment';
import { useIntl } from 'bundles/page/lib/useIntl';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import { getImagePropOverrides } from 'bundles/product-card/components/utils/productCardV2Utils';
import type { LEARNING_PRODUCTS } from 'bundles/search-common/constants';
import { getTranslatedDifficulty } from 'bundles/search-common/constants';

import _t from 'i18n!nls/unified-common';

type LearningProduct = (typeof LEARNING_PRODUCTS)[keyof typeof LEARNING_PRODUCTS];

type Props = {
  productCards?: CoachRecommendedProductCardsFragment[] | null;
};

const styles = {
  carousel: (cardCount: number) => css`
    display: grid;
    grid-template-columns: repeat(${cardCount}, 290px);
    gap: var(--cds-spacing-200);
    padding-top: var(--cds-spacing-200);
    padding-bottom: var(--cds-spacing-200);
  `,
};

const CoachRecommendedProductCards = ({ productCards }: Props): React.ReactElement | null => {
  const intl = useIntl();
  if (!productCards || productCards.length === 0) {
    return null;
  }

  const getProductDuration = (productCard: CoachRecommendedProductCardsFragment) => {
    if (productCard.avgLearningMonths) {
      return intl.formatMessage(_t('{months} {months, plural, =1 {month} other {months}}'), {
        months: productCard.avgLearningMonths,
      });
    }

    if (productCard.totalContentHours) {
      return intl.formatMessage(_t('{hours} {hours, plural, =1 {hour} other {hours}}'), {
        hours: productCard.totalContentHours,
      });
    }

    return undefined;
  };

  const getPartners = (productCard: CoachRecommendedProductCardsFragment) => {
    return productCard.partners?.map((partnerName, i) => ({
      id: partnerName,
      name: partnerName,
      logoUrl: productCard.partnerLogos?.[i] ?? undefined,
    }));
  };

  return (
    <OverflowCarousel css={styles.carousel(productCards.length)} shouldAllowSpaceForHoverItems>
      {productCards.map((productCard, idx) => (
        <LearningProductCard
          key={productCard.id}
          id={productCard.id}
          cardIndex={idx}
          productType={productCard.productType?.toUpperCase() as LearningProduct}
          variant="grid"
          name={productCard.name}
          titleComponent="h3"
          linkProps={{
            href: productCard.url,
            target: '_blank',
            rel: 'noopener',
          }}
          ImageComponent={
            <LearningProductCardImage
              imageUrl={productCard.imageUrl ?? ''}
              imageParams={{
                ...DESKTOP_LIST_IMAGE_CONFIG,
                ...getImagePropOverrides(getPartners(productCard) ?? [], productCard.imageUrl ?? ''),
              }}
            />
          }
          partners={getPartners(productCard)}
          productDifficultyLevel={getTranslatedDifficulty(productCard.difficultyLevel || '')}
          avgProductRating={
            productCard.averageFiveStarRating ? parseFloat(productCard.averageFiveStarRating.toFixed(1)) : undefined
          }
          productDuration={getProductDuration(productCard)}
        />
      ))}
    </OverflowCarousel>
  );
};

export default CoachRecommendedProductCards;
